import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getDefaultFiscalYearFilterModel } from "../Utils/filterUtils";
import Http from "../services/Http";
import dataService from "../services/data.service";

// Create context for user preferences
const UserPreferencesContext = createContext();

// Storage key (will be appended with userId)
const STORAGE_KEY = "user_preferences";

 const DEFAULT_ALL_PLANS_COLUMN_STATE = [
  { colId: "ag-Grid-SelectionColumn", width: 30, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: null },
  { colId: "propertyName", width: 220, hide: false, pinned: null, sort: "asc", sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "fiscalYear", width: 110, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "planTypeName", width: 120, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  // TPX-2011: Name column no longer flexes — its default width is content-driven
  // (auto-sized to the longest plan name in Plans.js). flex:null keeps a fresh default
  // view equal to this canonical default so Restore Defaults detection stays correct;
  // width here is only a fallback before auto-size runs.
  { colId: "versionName", width: 150, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: null },
  { colId: "versionNumber", width: 110, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "planStatusName", width: 120, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "modifiedOn", width: 160, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "modifiedBy", width: 120, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "entryMonth", width: 100, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "entryWeek", width: 95, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "asOnDate", width: 160, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "readOnly", width: 90, hide: false, pinned: null, sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 },
  { colId: "actions", width: 120, hide: false, pinned: "right", sort: null, sortIndex: null, aggFunc: null, rowGroup: false, rowGroupIndex: null, pivot: false, pivotIndex: null, flex: 1 }
];

const DEFAULT_PLAN_DETAILS_LINE_ITEMS = [
  {
    "colId": "ag-Grid-SelectionColumn",
    "width": 30,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": null
  },
  {
    "colId": "department_Name",
    "width": 180,
    "hide": false,
    "pinned": null,
    "sort": "asc",
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "functional_Code",
    "width": 120,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "functional_Code_Name",
    "width": 120,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "structure_Code",
    "width": 80,
    "hide": true,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "structure_Code_Name",
    "width": 120,
    "hide": true,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "account_Type_Id",
    "width": 145,
    "hide": true,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
    {
    "colId": "driver_Name",
    "width": 180,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Jan",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Jan",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Jan",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Feb",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Feb",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Feb",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Mar",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Mar",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Mar",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Apr",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Apr",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Apr",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_May",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_May",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_May",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Jun",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Jun",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Jun",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Jul",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Jul",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Jul",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Aug",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Aug",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Aug",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Sep",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Sep",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Sep",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Oct",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Oct",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Oct",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Nov",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Nov",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Nov",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "monthly_Total_Dec",
    "width": 130,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor_Dec",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpar_Dec",
    "width": 90,
    "hide": false,
    "pinned": null,
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "total",
    "width": 100,
    "hide": false,
    "pinned": "right",
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": 1
  },
  {
    "colId": "cpor-year",
    "width": 90,
    "hide": false,
    "pinned": "right",
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": null
  },
  {
    "colId": "cpar-year",
    "width": 90,
    "hide": false,
    "pinned": "right",
    "sort": null,
    "sortIndex": null,
    "aggFunc": null,
    "rowGroup": false,
    "rowGroupIndex": null,
    "pivot": false,
    "pivotIndex": null,
    "flex": null
  }
]

const DEFAULT_PLAN_DETAILS_PREFERENCES = [
    // {
    //     "colId": "ag-Grid-SelectionColumn",
    //     "width": 30,
    //     "hide": false,
    //     "pinned": null,
    //     "sort": null,
    //     "sortIndex": null,
    //     "aggFunc": null,
    //     "rowGroup": false,
    //     "rowGroupIndex": null,
    //     "pivot": false,
    //     "pivotIndex": null,
    //     "flex": null
    // },
    {
        "colId": "ag-Grid-AutoColumn",
        "width": 500,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": null
    },
    {
        "colId": "driver_Name",
        "width": 200,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": null
    },
    {
        "colId": "month-total",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "month-cpor",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "month-cpar",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "month-comp",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "month-var",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "month-var-percent",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "yearActuals",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "year-cpor",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "year-cpar",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "yearComp",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "yearVar",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "yearVarPercent",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "ytdActuals",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "ytd-cpor",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "ytd-cpar",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "ytdComp",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "ytdVar",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "ytdVarPercent",
        "width": 90,
        "hide": false,
        "pinned": null,
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": 1
    },
    {
        "colId": "action",
        "width": 120,
        "hide": false,
        "pinned": "right",
        "sort": null,
        "sortIndex": null,
        "aggFunc": null,
        "rowGroup": false,
        "rowGroupIndex": null,
        "pivot": false,
        "pivotIndex": null,
        "flex": null
    }
]


// Helper to deep clone default column states
const cloneState = (state) => JSON.parse(JSON.stringify(state));

// Normalize column state for comparison (ignore runtime-only properties)
const normalizeColumnState = (state) => {
  if (!Array.isArray(state)) return [];

  return state.map(
    ({
      colId,
      hide = false,
      pinned = null,
      sort = null,
      sortIndex = null,
      flex = null,
    }) => ({
      colId,
      hide: normalizeValue(hide),
      pinned: normalizeValue(pinned),
      sort: normalizeValue(sort),
      sortIndex: normalizeValue(sortIndex),
      flex: normalizeValue(flex),
    })
  );
};
const normalizeValue = (value) => {
  return value === 0 || value === undefined ? null : value;
};

const areColumnStatesEquivalent = (stateA, stateB) => {
  return JSON.stringify(normalizeColumnState(stateA)) === JSON.stringify(normalizeColumnState(stateB));
};

/**
 * Subset-aware comparison: returns true when every column in `actualState`
 * matches the corresponding entry in `defaultState` (by colId), preserves the
 * default's relative ordering, and contains no unknown columns.
 *
 * Required because conditional columns (e.g. comp/var/var-percent shown only
 * when `showPlanTableVarience` is true) make the actual column count smaller
 * than the canonical default, so a strict length-equality check would falsely
 * flag the view as customized and keep the Restore Defaults button visible.
 */
const isColumnStateSubsetOfDefault = (actualState, defaultState) => {
  if (!Array.isArray(actualState) || actualState.length === 0) return true;
  if (!Array.isArray(defaultState) || defaultState.length === 0) return false;

  const normalizedActual = normalizeColumnState(actualState);
  const normalizedDefault = normalizeColumnState(defaultState);

  const defaultByColId = new Map(normalizedDefault.map((col) => [col.colId, col]));
  const defaultOrder = new Map(normalizedDefault.map((col, idx) => [col.colId, idx]));

  let lastSeenIdx = -1;
  for (const col of normalizedActual) {
    const expected = defaultByColId.get(col.colId);
    if (!expected) return false;
    if (
      col.hide !== expected.hide ||
      col.pinned !== expected.pinned ||
      col.sort !== expected.sort ||
      col.sortIndex !== expected.sortIndex
    ) {
      return false;
    }
    const expectedIdx = defaultOrder.get(col.colId);
    if (expectedIdx <= lastSeenIdx) return false;
    lastSeenIdx = expectedIdx;
  }

  return true;
};

// Default preferences structure for the All Plans page.
// `filterModel` is AG Grid column-only. Page-level toggles
// (`hideCompleted`, `dueThisWeek`) live in `quickFilters` state in
// Plans.js and are persisted to the backend directly via saveUserPreference;
// they are intentionally NOT mirrored in this context's localStorage shape.
const DEFAULT_ALL_PLANS_PREFERENCES = {
  filterModel: null,
  denseView: false,
  tableView: "grid", // "list" | "grid" - default is grid (Card View)
  savedAt: null,
};


/**
 * Get user-scoped storage key
 * @param {string} userId - User email or identifier
 * @returns {string} - Storage key scoped to user
 */
const getUserStorageKey = (userId) => {
  if (!userId) {
    throw new Error("UserId is required for user-scoped preferences");
  }
  return `${STORAGE_KEY}_${userId}`;
};

/**
 * Get all user preferences from localStorage
 * @param {string} userId - User email or identifier
 * @returns {Object} - All user preferences
 */
const getAllUserPreferences = (userId) => {
  if (!userId) return null;
  
  try {
    const storageKey = getUserStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error("Error loading user preferences:", error);
  }
  return null;
};

/**
 * Save all user preferences to localStorage
 * @param {string} userId - User email or identifier
 * @param {Object} preferences - All user preferences object
 * @returns {boolean} - Success status
 */
const saveAllUserPreferences = (userId, preferences) => {
  if (!userId) {
    console.error("Cannot save preferences: User not authenticated");
    return false;
  }

  try {
    const storageKey = getUserStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(preferences));
    return true;
  } catch (error) {
    console.error("Error saving user preferences:", error);
    return false;
  }
};

/**
 * UserPreferencesProvider - Manages user preferences with localStorage persistence
 * 
 * This context provides a flexible and extensible way to store and retrieve
 * user preferences. It's designed to easily accommodate future additions.
 * Preferences are stored per user using user email as the identifier.
 * 
 * Storage structure:
 * {
 *   allPlanPreferences: { filterModel, denseView, savedAt, userId },
 *   planDetailsPreferences: {
 *     [planId]: { lineItemsView, nestedView, showNestedData, ... }
 *   }
 * }
 * 
 * @param {React.ReactNode} children - Child components
 */
export const UserPreferencesProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth0();
  const userId = user?.email || null;

  // State for All Plans page preferences
  const [allPlansPreferences, setAllPlansPreferences] = useState(DEFAULT_ALL_PLANS_PREFERENCES);

  // State for Plan Details preferences (keyed by planId)
  const [planDetailsPreferences, setPlanDetailsPreferences] = useState({});

  // Reload preferences when user changes
  useEffect(() => {
    if (!userId || !isAuthenticated) {
      setAllPlansPreferences(DEFAULT_ALL_PLANS_PREFERENCES);
      setPlanDetailsPreferences({});
      return;
    }

    try {
      const allPrefs = getAllUserPreferences(userId);
      if (allPrefs) {
        if (allPrefs.allPlanPreferences) {
          setAllPlansPreferences({
            ...DEFAULT_ALL_PLANS_PREFERENCES,
            ...allPrefs.allPlanPreferences,
          });
        } else {
          setAllPlansPreferences(DEFAULT_ALL_PLANS_PREFERENCES);
        }

        // Load Plan Details preferences
        if (allPrefs.planDetailsPreferences) {
          setPlanDetailsPreferences(allPrefs.planDetailsPreferences);
        } else {
          setPlanDetailsPreferences({});
        }
      } else {
        setAllPlansPreferences(DEFAULT_ALL_PLANS_PREFERENCES);
        setPlanDetailsPreferences({});
      }
    } catch (error) {
      console.error("Error loading user preferences:", error);
      setAllPlansPreferences(DEFAULT_ALL_PLANS_PREFERENCES);
      setPlanDetailsPreferences({});
    }
  }, [userId, isAuthenticated]);

  /**
   * Save All Plans preferences
   * @param {Object} preferences - Preferences object containing filterModel and denseView
   * @returns {boolean} - Success status
   */
  const saveAllPlansPreferences = useCallback((preferences) => {
    if (!userId) {
      console.error("Cannot save preferences: User not authenticated");
      return false;
    }

    try {
      const preferencesToSave = {
        filterModel: preferences.filterModel || null,
        denseView: preferences.denseView ?? false,
        tableView: preferences.tableView ?? "grid",
        savedAt: new Date().toISOString(),
        userId: userId,
      };

      // Get all existing preferences
      const allPrefs = getAllUserPreferences(userId) || {};
      
      // Update allPlanPreferences
      const updatedPrefs = {
        ...allPrefs,
        allPlanPreferences: preferencesToSave,
      };

      // Save to localStorage
      if (saveAllUserPreferences(userId, updatedPrefs)) {
        setAllPlansPreferences(preferencesToSave);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error saving All Plans preferences:", error);
      return false;
    }
  }, [userId]);

  /**
   * Reset All Plans preferences to default
   * @returns {boolean} - Success status
   */
  const resetAllPlansPreferences = useCallback(() => {
    if (!userId) {
      console.error("Cannot reset preferences: User not authenticated");
      return false;
    }

    try {
      // Get all existing preferences
      const allPrefs = getAllUserPreferences(userId) || {};
      
      // Reset allPlanPreferences to defaults; keep planDetailsPreferences
      const freshDefaults = { ...DEFAULT_ALL_PLANS_PREFERENCES };
      const updatedPrefs = {
        ...allPrefs,
        allPlanPreferences: freshDefaults,
      };

      // Save to localStorage
      if (saveAllUserPreferences(userId, updatedPrefs)) {
        setAllPlansPreferences(freshDefaults);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error resetting All Plans preferences:", error);
      return false;
    }
  }, [userId]);

  /**
   * Get All Plans preferences
   * @returns {Object} - Current preferences
   */
  const getAllPlansPreferences = useCallback(() => {
    return allPlansPreferences;
  }, [allPlansPreferences]);

  /**
   * Check if preferences exist
   * @returns {boolean} - True if preferences are saved
   */
  const hasAllPlansPreferences = useCallback(() => {
    return allPlansPreferences.savedAt !== null;
  }, [allPlansPreferences.savedAt]);


  /**
   * Normalize filter model by removing filterType and other AG Grid metadata
   * This ensures consistent comparison regardless of how AG Grid stores the filter
   * @param {Object} model - Filter model to normalize
   * @returns {Object} - Normalized filter model
   */
  const normalizeFilterModel = (model) => {
    if (!model || Object.keys(model).length === 0) return null;
    
    const normalized = {};
    
    Object.keys(model).forEach(key => {
      const filter = model[key];
      
      if (filter && typeof filter === 'object') {
        const normalizedFilter = {};
        
        // Copy essential properties, excluding filterType
        if (filter.operator !== undefined) {
          normalizedFilter.operator = filter.operator;
        }
        if (filter.type !== undefined) {
          normalizedFilter.type = filter.type;
        }
        if (filter.filter !== undefined) {
          normalizedFilter.filter = filter.filter;
        }
        if (filter.conditions !== undefined && Array.isArray(filter.conditions)) {
          // Recursively normalize conditions
          normalizedFilter.conditions = filter.conditions.map(condition => {
            const normalizedCondition = {};
            if (condition.type !== undefined) {
              normalizedCondition.type = condition.type;
            }
            if (condition.filter !== undefined) {
              normalizedCondition.filter = condition.filter;
            }
            return normalizedCondition;
          });
        }
        
        // Only add if filter has meaningful content
        if (Object.keys(normalizedFilter).length > 0) {
          normalized[key] = normalizedFilter;
        }
      }
    });
    
    return Object.keys(normalized).length > 0 ? normalized : null;
  };

  /**
   * Compare two filter models to check if they're equivalent
   * @param {Object} filterModel1 - First filter model
   * @param {Object} filterModel2 - Second filter model
   * @returns {boolean} - True if filter models are equivalent
   */
  const areFilterModelsEquivalent = (filterModel1, filterModel2) => {
    if (!filterModel1 && !filterModel2) return true;
    if (!filterModel1 || !filterModel2) return false;
    
    // Normalize both filter models for comparison (removes filterType and other metadata)
    const normalized1 = normalizeFilterModel(filterModel1);
    const normalized2 = normalizeFilterModel(filterModel2);
    
    // Compare normalized models
    return JSON.stringify(normalized1) === JSON.stringify(normalized2);
  };

  /**
   * Check if All Plans preferences are at default values
   * @returns {boolean} - True if preferences match defaults
   */
  const isAllPlansPreferencesDefault = useCallback(() => {
    const prefs = allPlansPreferences;
    if (prefs.denseView !== false) return false;
    if (prefs.tableView !== "grid") return false;

    // filterModel is AG Grid column-only; compare against the default fiscal-year filter.
    if (prefs.filterModel && Object.keys(prefs.filterModel).length > 0) {
      const defaultFilterModel = getDefaultFiscalYearFilterModel();
      if (!areFilterModelsEquivalent(prefs.filterModel, defaultFilterModel)) {
        return false;
      }
    }
    // If filterModel is null/empty, treat as default (default filter is
    // applied programmatically by the page).

    return true;
  }, [allPlansPreferences]);

  const getDefaultAllPlansColumnState = useCallback(() => cloneState(DEFAULT_ALL_PLANS_COLUMN_STATE), []);

  const isAllPlansColumnStateDefault = useCallback(
    (columnState) => areColumnStatesEquivalent(columnState, DEFAULT_ALL_PLANS_COLUMN_STATE),
    []
  );

  /**
   * Get Plan Details preferences for a specific planId
   * @param {string} planId - Plan ID
   * @returns {Object} - Plan Details preferences for the planId
   */
  const getPlanDetailsPreferences = useCallback((planId) => {
    if (!planId) return DEFAULT_PLAN_DETAILS_PREFERENCES;
    return planDetailsPreferences[planId] || DEFAULT_PLAN_DETAILS_PREFERENCES;
  }, [planDetailsPreferences]);

  /**
   * Save Plan Details preferences for a specific planId
   * @param {string} planId - Plan ID
   * @param {Object} preferences - Preferences object
   * @returns {boolean} - Success status
   */
  const savePlanDetailsPreferences = useCallback((planId, preferences) => {
    if (!userId) {
      console.error("Cannot save preferences: User not authenticated");
      return false;
    }

    if (!planId) {
      console.error("Cannot save preferences: PlanId is required");
      return false;
    }

    try {
      const preferencesToSave = {
        ...DEFAULT_PLAN_DETAILS_PREFERENCES,
        ...preferences,
        savedAt: new Date().toISOString(),
        userId: userId,
      };

      // Get all existing preferences
      const allPrefs = getAllUserPreferences(userId) || {};
      
      // Update planDetailsPreferences for this planId
      const updatedPrefs = {
        ...allPrefs,
        planDetailsPreferences: {
          ...(allPrefs.planDetailsPreferences || {}),
          [planId]: preferencesToSave,
        },
      };

      // Save to localStorage
      if (saveAllUserPreferences(userId, updatedPrefs)) {
        setPlanDetailsPreferences(prev => ({
          ...prev,
          [planId]: preferencesToSave,
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error saving Plan Details preferences:", error);
      return false;
    }
  }, [userId]);

  /**
   * Reset Plan Details preferences for a specific planId to default
   * @param {string} planId - Plan ID
   * @returns {boolean} - Success status
   */
  const resetPlanDetailsPreferences = useCallback((planId) => {
    if (!userId) {
      console.error("Cannot reset preferences: User not authenticated");
      return false;
    }

    if (!planId) {
      console.error("Cannot reset preferences: PlanId is required");
      return false;
    }

    try {
      // Get all existing preferences
      const allPrefs = getAllUserPreferences(userId) || {};
      
      // Remove planDetailsPreferences for this planId
      const updatedPlanDetailsPrefs = { ...(allPrefs.planDetailsPreferences || {}) };
      delete updatedPlanDetailsPrefs[planId];

      const updatedPrefs = {
        ...allPrefs,
        planDetailsPreferences: updatedPlanDetailsPrefs,
      };

      // Save to localStorage
      if (saveAllUserPreferences(userId, updatedPrefs)) {
        setPlanDetailsPreferences(prev => {
          const updated = { ...prev };
          delete updated[planId];
          return updated;
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error resetting Plan Details preferences:", error);
      return false;
    }
  }, [userId]);

  /**
   * Check if Plan Details preferences exist for a specific planId
   * @param {string} planId - Plan ID
   * @returns {boolean} - True if preferences are saved
   */
  const hasPlanDetailsPreferences = useCallback((planId) => {
    if (!planId) return false;
    const prefs = planDetailsPreferences[planId];
    return prefs && prefs.savedAt !== null;
  }, [planDetailsPreferences]);

  /**
   * Check if Plan Details preferences are at default values for a specific planId
   * @param {string} planId - Plan ID
   * @returns {boolean} - True if preferences match defaults
   */
  const isPlanDetailsPreferencesDefault = useCallback((planId) => {
    if (!planId) return true;
    if (!hasPlanDetailsPreferences(planId)) return true;
    
    const prefs = planDetailsPreferences[planId];
    
    // Check all default values
    if (prefs.showNestedData !== true) return false;
    if (prefs.isShowDriverDetailsChecked !== false) return false;
    if (prefs.hideZeroRows !== true) return false;
    if (prefs.showOnlyPairedData !== false) return false;
    if (prefs.denseView !== false) return false;
    
    // Check filter models
    const lineItemsFilterModel = prefs.lineItemsView?.filterModel;
    const nestedFilterModel = prefs.nestedView?.filterModel;
    
    if (lineItemsFilterModel && Object.keys(lineItemsFilterModel).length > 0) {
      return false;
    }
    if (nestedFilterModel && Object.keys(nestedFilterModel).length > 0) {
      return false;
    }
    
    return true;
  }, [planDetailsPreferences, hasPlanDetailsPreferences]);

  const getDefaultPlanDetailsColumnState = useCallback(
    () => cloneState(DEFAULT_PLAN_DETAILS_PREFERENCES),
    []
  );

    const getDefaultPlanDetailsLineItemsColumnState = useCallback(
    () => cloneState(DEFAULT_PLAN_DETAILS_LINE_ITEMS),
    []
  );

const isPlanDetailsColumnStateDefault = useCallback(
  (columnState) => {
    // Empty/missing state means the grid hasn't initialized yet — treat as default.
    if (!columnState || columnState.length === 0) return true;

    return isColumnStateSubsetOfDefault(
      columnState,
      DEFAULT_PLAN_DETAILS_PREFERENCES
    );
  },
  []
);

  // Settings closed-month grid state, per HMG (localStorage only)
  const getSettingsPreferences = useCallback((hmgId) => {
    if (!userId) return null;
    return getAllUserPreferences(userId)?.settingsPreferences?.[hmgId] ?? null;
  }, [userId]);

  const saveSettingsPreferences = useCallback((hmgId, preferences) => {
    if (!userId) return;
    const allPrefs = getAllUserPreferences(userId) || {};
    saveAllUserPreferences(userId, {
      ...allPrefs,
      settingsPreferences: { ...allPrefs.settingsPreferences, [hmgId]: preferences },
    });
  }, [userId]);

  // In-flight de-dup + cache for user-preference reads, keyed by `org|preferenceType|planId`, so the
  // several components that read the same preference on Plan Details load share ONE request
  // (TPX-1856). Cache entries are invalidated on EVERY write path (save + delete + the HMGContext
  // save/discard event) so reads always stay fresh.
  const userPrefCacheRef = useRef(new Map());
  const userPrefInflightRef = useRef(new Map());

  const deleteUserPreference = useCallback(
    async ({ preferenceType, planId }) => {
      const organizationId = dataService.getSelectedHMGId();
      if (!organizationId) {
        console.error("Missing organizationId for deleteUserPreference");
        return null;
      }
      if (!preferenceType) {
        console.error("Missing preferenceType for deleteUserPreference");
        return null;
      }
      try {
        const url = `/user-preferences/delete?organizationId=${organizationId}&preferenceType=${encodeURIComponent(preferenceType)}${planId ? `&planId=${planId}` : ""}`;
        const result = await Http.DELETE(url);
        // Invalidate the cached read for this key so the next getUserPreference refetches (H-01).
        const cacheKey = `${organizationId}|${preferenceType}|${planId ?? ""}`;
        userPrefCacheRef.current.delete(cacheKey);
        userPrefInflightRef.current.delete(cacheKey);
        return result;
      } catch (err) {
        console.error("Failed to delete user preference", err);
        return null;
      }
    },
    []
  );

  const saveUserPreference = useCallback(async ({ preferenceType, planId, preferenceData, organizationId: explicitOrgId }) => {
    const organizationId = explicitOrgId || dataService.getSelectedHMGId();
    if (!organizationId || !preferenceType) {
      console.error("Missing organizationId or preferenceType for saveUserPreference");
      return null;
    }
    const payload = {
      organizationId,
      planId: planId ?? null,
      preferenceType,
      preferenceData,
    };
    try {
      // TPX-2471: best-effort autosave — suppress the global no-response toast.
      const result = await Http.POST("/user-preferences/save", payload, { _silent: true });
      // Invalidate the cached read for this key so the next getUserPreference refetches fresh.
      userPrefCacheRef.current.delete(`${organizationId}|${preferenceType}|${planId ?? ""}`);
      return result;
    } catch (err) {
      console.error("Failed to save user preference", err);
      return null;
    }
  }, []);

  const getUserPreference = useCallback(async ({ preferenceType, planId, forceRefresh = false }) => {
    const organizationId = dataService.getSelectedHMGId();
    if (!organizationId || !preferenceType) {
      console.error("Missing organizationId or preferenceType for getUserPreference");
      return null;
    }
    const key = `${organizationId}|${preferenceType}|${planId ?? ""}`;
    // TPX-2127: forceRefresh drops the cached/in-flight entry so a fresh GET runs — used on All Plans
    // (re)load so the cache never serves a value written just before the @Async backend save committed.
    if (forceRefresh) {
      userPrefCacheRef.current.delete(key);
      userPrefInflightRef.current.delete(key);
    }
    if (userPrefCacheRef.current.has(key)) return userPrefCacheRef.current.get(key);
    if (userPrefInflightRef.current.has(key)) return userPrefInflightRef.current.get(key);
    const pending = (async () => {
      try {
        // TPX-2471: best-effort read — suppress the global no-response toast.
        const resp = await Http.GET(
          `/user-preferences/get?organizationId=${organizationId}&preferenceType=${encodeURIComponent(
            preferenceType
          )}${planId ? `&planId=${planId}` : ""}`,
          {},
          { _silent: true }
        );
        const data = resp?.data || null;
        userPrefCacheRef.current.set(key, data);
        return data;
      } catch (err) {
        if (err?.response?.status === 404) {
          userPrefCacheRef.current.set(key, null); // no preference saved — cache the negative result
          return null;
        }
        console.error("Failed to get user preference", err);
        return null; // do not cache non-404 errors — allow a later retry
      } finally {
        userPrefInflightRef.current.delete(key);
      }
    })();
    userPrefInflightRef.current.set(key, pending);
    return pending;
  }, []);

  // M-01: HMGContext resets PLAN_DETAILS_LINE_ITEMS_VIEW (showOnlyUnSavedData) server-side on
  // save/discard via a direct POST that bypasses saveUserPreference. It fires this event; clear the
  // matching cache key so the next read reflects the reset instead of a stale cached value.
  useEffect(() => {
    const onPlanSaved = (e) => {
      const planId = e?.detail?.planId;
      if (planId == null) return;
      const organizationId = dataService.getSelectedHMGId();
      if (!organizationId) return;
      userPrefCacheRef.current.delete(`${organizationId}|PLAN_DETAILS_LINE_ITEMS_VIEW|${planId}`);
    };
    window.addEventListener("plan-details-final-saved", onPlanSaved);
    return () => window.removeEventListener("plan-details-final-saved", onPlanSaved);
  }, []);

  const value = useMemo(() => ({
    // All Plans preferences
    allPlansPreferences,
    saveAllPlansPreferences,
    resetAllPlansPreferences,
    getAllPlansPreferences,
    hasAllPlansPreferences,
    isAllPlansPreferencesDefault,
    isAllPlansColumnStateDefault,
    getDefaultAllPlansColumnState,

    // Plan Details preferences
    getPlanDetailsPreferences,
    savePlanDetailsPreferences,
    resetPlanDetailsPreferences,
    hasPlanDetailsPreferences,
    isPlanDetailsPreferencesDefault,
    isPlanDetailsColumnStateDefault,
    getDefaultPlanDetailsColumnState,
    getDefaultPlanDetailsLineItemsColumnState,

    getSettingsPreferences,
    saveSettingsPreferences,

    // Backend persistence
    saveUserPreference,
    getUserPreference,
    deleteUserPreference,
  }), [
    allPlansPreferences,
    saveAllPlansPreferences,
    resetAllPlansPreferences,
    getAllPlansPreferences,
    hasAllPlansPreferences,
    isAllPlansPreferencesDefault,
    isAllPlansColumnStateDefault,
    getDefaultAllPlansColumnState,
    getPlanDetailsPreferences,
    savePlanDetailsPreferences,
    resetPlanDetailsPreferences,
    hasPlanDetailsPreferences,
    isPlanDetailsPreferencesDefault,
    isPlanDetailsColumnStateDefault,
    getDefaultPlanDetailsColumnState,
    getDefaultPlanDetailsLineItemsColumnState,
    getSettingsPreferences,
    saveSettingsPreferences,
    saveUserPreference,
    getUserPreference,
    deleteUserPreference,
  ]);

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

/**
 * Custom hook to access user preferences
 * @returns {Object} - User preferences context value
 */
export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error(
      "useUserPreferences must be used within a UserPreferencesProvider"
    );
  }
  return context;
};

